import type { ToasterProps } from 'react-hot-toast';

import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Toaster as ReactHotToaster } from 'react-hot-toast';

import useClassy from '@core/hooks/useClassy';
import useEnvInfo from '@core/hooks/useEnvInfo';

import styles from './Toaster.module.scss';

/**
 * A less opionated toaster that applies base styles like z-index but can be
 * placed anywhere in the document. Use this when you want to customize the
 * position of notifications.
 *
 * @see https://react-hot-toast.com/docs/styling#change-position-of-the-toaster
 * @see https://react-hot-toast.com/docs/multi-toaster
 */
export const BaseToaster = ({ containerStyle, ...rest }: ToasterProps) => {
  const bem = useClassy(styles, 'Toaster');

  return (
    <ReactHotToaster
      containerClassName={bem()}
      containerStyle={{
        // Intentionally undefined so we can set z-index in scss.
        zIndex: undefined,
        ...containerStyle,
      }}
      {...rest}
    />
  );
};

/**
 * A portal'd wrapper around `BaseToaster` that positions notifications in the
 * bottom right of the viewport.
 */
const PortalToaster = (props: ToasterProps) => {
  const { isClient } = useEnvInfo();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Skip rendering for SSR
  if (!mounted || !isClient) return null;

  return ReactDOM.createPortal(<BaseToaster {...props} />, document.body);
};

export default PortalToaster;
